// Casca Aeros Dark do modal de conexão (WhatsAppModal).
//
// O modal tem ~2.100 linhas e ~60 campos herdados; em vez de reescrever o
// JSX, o padrão visual entra por regras escopadas ao corpo do modal — os
// campos MUI existentes recebem o mesmo tratamento dos modais Aeros novos.
//
// Armadilha conhecida: nunca aplicar `minHeight` em
// `.MuiOutlinedInput-inputMultiline`. Ele briga com a textarea-sombra do
// TextareaAutosize e estoura a altura do campo (~400px). Use `minRows`.
import { makeStyles, alpha } from "@material-ui/core/styles";
import { AEROS, stateText } from "../../styles/aerosTokens";

const useWhatsAppModalStyles = makeStyles((theme) => {
  const isLight = theme.palette.type === "light";
  const border = isLight ? theme.palette.divider : "rgba(255,255,255,0.07)";
  const borderInput = isLight
    ? theme.palette.aeros.borderStrong
    : "rgba(255,255,255,0.1)";

  return {
    paper: {
      width: 900,
      maxWidth: "calc(100vw - 32px)",
      maxHeight: "calc(var(--vh-full) - 48px)",
      margin: 16,
    },
    form: {
      display: "flex",
      flexDirection: "column",
      minHeight: 0,
      maxHeight: "inherit",
    },
    header: {
      padding: "20px 24px 16px",
      display: "flex",
      alignItems: "center",
      gap: 14,
      borderBottom: `1px solid ${border}`,
      flexShrink: 0,
    },
    headerIcon: {
      width: 38,
      height: 38,
      borderRadius: 12,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      background: alpha(AEROS.defaultPrimary, 0.14),
      color: stateText(theme, "#C08BFF", "purple"),
      flexShrink: 0,
      "& .MuiSvgIcon-root": { fontSize: 19 },
    },
    title: {
      fontFamily: AEROS.fontDisplay,
      fontSize: 16,
      fontWeight: 700,
      letterSpacing: "-0.01em",
      color: theme.palette.text.primary,
    },
    subtitle: { fontSize: 12, color: theme.palette.text.secondary, marginTop: 2 },
    closeButton: {
      marginLeft: "auto",
      padding: 4,
      color: theme.palette.aeros.textTertiary,
      "& .MuiSvgIcon-root": { fontSize: 20 },
    },

    tabsPaper: { backgroundColor: "transparent", flexShrink: 0 },
    tabScrollButton: {
      width: 28,
      color: theme.palette.aeros.textTertiary,
    },

    // Abas: sublinhado roxo na ativa.
    tabs: {
      flexShrink: 0,
      minHeight: 44,
      padding: "0 12px",
      borderBottom: `1px solid ${border}`,
      "& .MuiTabs-indicator": { backgroundColor: AEROS.defaultPrimary, height: 2 },
      "& .MuiTabs-flexContainer": { gap: 2 },
    },
    tab: {
      minHeight: 44,
      minWidth: "auto",
      padding: "0 14px",
      textTransform: "none",
      fontSize: 13,
      fontWeight: 600,
      color: theme.palette.text.secondary,
      opacity: 1,
      "&.Mui-selected": { color: theme.palette.text.primary },
    },

    // Corpo: um único escopo estiliza todos os campos herdados.
    body: {
      overflowY: "auto",
      minHeight: 0,
      ...theme.scrollbarStyles,
      "& .MuiDialogContent-root": {
        padding: "20px 24px",
        borderTop: "none",
        borderBottom: "none",
      },
      "& .MuiOutlinedInput-root": {
        borderRadius: 10,
        backgroundColor: theme.palette.aeros.surface,
        fontSize: 13.5,
      },
      "& .MuiOutlinedInput-notchedOutline": { borderColor: borderInput },
      "& .MuiInputLabel-root": { fontSize: 13.5 },
      "& .MuiSelect-icon": { color: theme.palette.text.secondary },
      "& .MuiFormHelperText-root": {
        fontSize: 11,
        color: theme.palette.aeros.textTertiary,
      },
      "& .MuiPaper-root": { backgroundColor: "transparent", boxShadow: "none" },
    },
    // Painel de aba: o gap substitui as margens soltas do layout antigo.
    panel: {
      "& .MuiDialogContent-root": {
        display: "flex",
        flexDirection: "column",
        gap: 14,
      },
    },

    // ---- aba Geral ----
    // linha 1 do mockup: [Imagem] [Nome *] [Cor]
    topRow: {
      display: "grid",
      gridTemplateColumns: "auto minmax(0,1fr) 190px",
      gap: 10,
      alignItems: "end",
      [theme.breakpoints.down("xs")]: { gridTemplateColumns: "1fr" },
    },
    imageButton: {
      height: 38,
      borderRadius: 10,
      padding: "0 14px",
      textTransform: "none",
      fontSize: 12.5,
      fontWeight: 600,
      whiteSpace: "nowrap",
      color: theme.palette.text.secondary,
      border: `1px dashed ${
        isLight ? theme.palette.aeros.borderStrong : "rgba(255,255,255,0.16)"
      }`,
      "& .MuiSvgIcon-root": { fontSize: 17 },
    },
    switchGrid: {
      display: "grid",
      gridTemplateColumns: "repeat(3, minmax(0,1fr))",
      gap: 10,
      [theme.breakpoints.down("xs")]: { gridTemplateColumns: "1fr" },
    },
    // caixa do toggle: some com a margem que o FormControlLabel traz do MUI
    switchBox: {
      "& .MuiFormControlLabel-root": { margin: 0 },
      "& .MuiFormControlLabel-label": { display: "none" },
      "& .MuiSwitch-root": { marginLeft: "auto", flexShrink: 0 },
    },
    colorSwatch: {
      width: 18,
      height: 18,
      borderRadius: 5,
      border: `1px solid ${borderInput}`,
    },
    warnBox: {
      display: "flex",
      alignItems: "flex-start",
      gap: 8,
      padding: "10px 12px",
      borderRadius: 10,
      fontSize: 11.5,
      lineHeight: 1.5,
      color: stateText(theme, AEROS.warning, "warning"),
      backgroundColor: alpha(AEROS.warning, 0.1),
      border: `1px solid ${alpha(AEROS.warning, 0.25)}`,
      "& .MuiSvgIcon-root": { fontSize: 15, flexShrink: 0, marginTop: 1 },
    },
    tokenRow: { display: "flex", alignItems: "flex-end", gap: 6 },
    iconAction: {
      padding: 7,
      color: theme.palette.text.secondary,
      border: `1px solid ${borderInput}`,
      borderRadius: 10,
      "& .MuiSvgIcon-root": { fontSize: 16 },
    },
    sectionTitle: {
      fontSize: 13,
      fontWeight: 700,
      color: theme.palette.text.primary,
    },
    sectionDesc: {
      fontSize: 11.5,
      color: theme.palette.text.secondary,
      marginTop: 2,
      marginBottom: 10,
    },
    // ---- aba Chatbot ----
    botGrid: {
      display: "grid",
      gridTemplateColumns: "repeat(3, minmax(0,1fr))",
      gap: 10,
      [theme.breakpoints.down("sm")]: {
        gridTemplateColumns: "repeat(2, minmax(0,1fr))",
      },
      [theme.breakpoints.down("xs")]: { gridTemplateColumns: "1fr" },
    },
    botCard: {
      padding: 12,
      borderRadius: 12,
      backgroundColor: theme.palette.aeros.surface,
      border: `1px solid ${border}`,
      display: "flex",
      flexDirection: "column",
      minWidth: 0,
    },
    botCardHead: {
      display: "flex",
      alignItems: "center",
      gap: 7,
      fontSize: 12.5,
      fontWeight: 600,
      color: theme.palette.text.primary,
      "& .MuiSvgIcon-root": {
        fontSize: 16,
        color: stateText(theme, "#C08BFF", "purple"),
      },
    },
    botCardHint: {
      fontSize: 11,
      lineHeight: 1.45,
      color: theme.palette.text.secondary,
      margin: "5px 0 9px",
      flexGrow: 1,
    },
    botCardField: { display: "flex", alignItems: "center", gap: 7 },
    botCardUnit: {
      fontSize: 11,
      fontWeight: 600,
      color: theme.palette.aeros.textTertiary,
      flexShrink: 0,
    },
    footer: {
      padding: "16px 24px",
      borderTop: `1px solid ${border}`,
      display: "flex",
      alignItems: "center",
      gap: 10,
      flexShrink: 0,
    },
    footerActions: { marginLeft: "auto", display: "flex", gap: 10 },
    cancelButton: {
      border: `1px solid ${
        isLight ? theme.palette.aeros.borderStrong : "rgba(255,255,255,0.16)"
      }`,
      color: theme.palette.text.primary,
      borderRadius: 10,
      padding: "7px 16px",
      fontWeight: 600,
      fontSize: 13,
    },
    submitButton: {
      borderRadius: 10,
      padding: "7px 18px",
      fontWeight: 700,
      fontSize: 13,
      position: "relative",
    },
  };
});

export default useWhatsAppModalStyles;
